import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getCatalog, getOwnership } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePetPrices } from '../context/CatalogContext';
import { PetVisual, ViewModeToggle } from '../components/visuals';
import { RELEASE_PETS, RELEASE_PETS_PNG_ONLY, RELEASE_PET_IDS, RELEASE_SHORT } from '../components/release/releaseCatalog';
import '../styles/release.css';
import './Catalog.css';


const TIER_LABELS = { free: 'Free', plus: 'Plus', premium: 'Premium', individual: 'Individual' };

export default function CatalogPetView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const PET_PRICES = usePetPrices();
  const [pet, setPet] = useState(null);
  // A tap on the pet and it waves, as it does on the phone; the count
  // restarts the clip on the next tap.
  const [wave, setWave] = useState(0);
  useEffect(() => {
    if (!wave) return undefined;
    const t = setTimeout(() => setWave(0), 2400);
    return () => clearTimeout(t);
  }, [wave]);
  const [owned, setOwned] = useState(false);
  const [loading, setLoading] = useState(true);

  // Local fallback data if backend doesn't have a row yet for the new release pets.
  const releaseMeta = [...RELEASE_PETS, ...RELEASE_PETS_PNG_ONLY].find((p) => p.id === id);

  useEffect(() => {
    let cancelled = false;
    getCatalog()
      .then((res) => {
        if (cancelled) return;
        const match = (res.data.pets || []).find((p) => p.id === id);
        setPet(match || (releaseMeta ? {
          id,
          name: releaseMeta.name,
          tier: 'individual',
          variants: null,
          supports_baby: false,
        } : null));
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    if (isAuthenticated) {
      getOwnership()
        .then((res) => !cancelled && setOwned(!!res.data.owned_pets?.[id]))
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [id, isAuthenticated, releaseMeta]);

  if (loading) return <div className="catalog-page"><div className="container">Loading...</div></div>;

  if (!pet) {
    return (
      <div className="catalog-page">
        <div className="container">
          <div className="catalog-header">
            <h1>Pet not found</h1>
            <p className="catalog-subtitle">We couldn't find that companion. It may have wandered off.</p>
          </div>
          <Link to="/catalog" className="btn btn-secondary">Back to Catalog</Link>
        </div>
      </div>
    );
  }

  const tier = pet.tier || 'individual';
  const price = PET_PRICES[tier] ?? 0;
  // The picture follows the site's 2D/3D switch (PetVisual).
  const blurb = releaseMeta?.blurb;
  const isReleasePet = RELEASE_PET_IDS.has(pet.id);

  return (
    <div className="catalog-page">
      <div className="container">
        <div className="catalog-header" style={{ marginBottom: 16 }}>
          <Link to="/catalog" className="btn btn-ghost btn-sm">← All pets</Link>
          <ViewModeToggle />
        </div>

        <div className="card detail-inner release-detail-card" style={{ maxWidth: 880, margin: '0 auto' }}>
          <div className="detail-header" style={{ alignItems: 'center', gap: 24 }}>
            <div
              className="detail-avatar-lg release-bloom"
              style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              aria-label={`Say hi to ${pet.name}`}
              onClick={() => setWave((n) => n + 1)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWave((n) => n + 1); } }}
            >
              <PetVisual petId={pet.id} size={220} mood="neutral" live showMissing clip={wave ? 'wave' : undefined} clipKey={wave} />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <h1 style={{ marginBottom: 8 }}>{pet.name}</h1>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className={`tier-badge tier-${tier}`}>{TIER_LABELS[tier]}</span>
                {tier === 'individual' && (
                  <span className="catalog-earnable-pill" title="Available with treats. Earn treats by logging in and during events.">
                    Earnable
                  </span>
                )}
                {isReleasePet && (
                  <span className="catalog-earnable-pill">{RELEASE_SHORT}</span>
                )}
              </div>
              {blurb && <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>{blurb}</p>}

              {pet.variants && pet.variants.length > 0 && (
                <div className="detail-variants" style={{ marginBottom: 16 }}>
                  <h4 style={{ marginBottom: 6 }}>Variants</h4>
                  <div className="variant-chips">
                    {pet.variants.map((v) => (
                      <span key={v} className="variant-chip">
                        {v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {tier !== 'free' && !owned ? (
                <div className="detail-locked">
                  <p className="detail-locked-msg">
                    Unlock {pet.name} for <strong>{price} treats</strong>.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={() => navigate('/shop')}>
                      Earn treats
                    </button>
                    {!isAuthenticated && (
                      <Link to="/login" className="btn btn-ghost">Sign in</Link>
                    )}
                  </div>
                </div>
              ) : (
                <Link to="/catalog" className="btn btn-primary">
                  {owned ? 'Adopt now in catalog' : 'Adopt now'}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
